import type { CSSProperties } from "react";

/**
 * Helpers compartidos para pintar los colores de `ReservationStatus` (bgColor/textColor,
 * configurables por el superadmin) tolerando AMBOS formatos durante la migración de BD:
 * hoy son clases Tailwind ('bg-yellow-100', 'text-yellow-700'), van a pasar a ser HEX
 * ('#FEF9C3', '#A16207'). Sin este helper, cualquiera de los dos formatos rompe la pantalla:
 * una clase Tailwind como valor de `style` no hace nada, y un HEX como `className` tampoco.
 *
 * Usado por `pages/Reservations.tsx` (badges, mapa de mesas, leyenda, botones de estado) y
 * por `components/TablePopover.tsx` (mismo tipo de badges/botones dentro del popover de mesa).
 */

export interface ColorProps {
  className: string;
  style: CSSProperties;
}

/** Propiedad CSS destino para un valor de color de estado. */
export type StatusCssProp = "backgroundColor" | "color" | "borderColor";

/**
 * - Si `value` empieza por '#' → se aplica como ESTILO INLINE en `cssProp`.
 * - Si es una clase Tailwind → se aplica como `className`, sin estilo inline.
 */
export function colorProps(
  value: string | undefined | null,
  cssProp: StatusCssProp,
): ColorProps {
  if (!value) return { className: "", style: {} };
  if (value.startsWith("#")) {
    return { className: "", style: { [cssProp]: value } };
  }
  return { className: value, style: {} };
}

/** Combina varios resultados de `colorProps` en un único { className, style } listo para spread. */
export function mergeColorProps(...parts: ColorProps[]): ColorProps {
  return {
    className: parts
      .map((p) => p.className)
      .filter(Boolean)
      .join(" "),
    style: Object.assign({}, ...parts.map((p) => p.style)),
  };
}

/** Fondo + texto de un badge de estado (bgColor + textColor/color de ReservationStatus). Tolera Tailwind y HEX. */
export function statusBadgeColorProps(
  bgColor?: string | null,
  textColor?: string | null,
): ColorProps {
  return mergeColorProps(
    colorProps(bgColor, "backgroundColor"),
    colorProps(textColor, "color"),
  );
}

/**
 * Fondo + borde (derivado de textColor) + texto para pintar una mesa del mapa según el estado
 * de su reserva. El borde no tiene campo propio en BD: en HEX se reutiliza el mismo textColor;
 * en Tailwind se deriva sustituyendo el prefijo 'text-' por 'border-'.
 */
export function tableStatusColorProps(
  bgColor?: string | null,
  textColor?: string | null,
): ColorProps {
  const borderSource = !textColor
    ? undefined
    : textColor.startsWith("#")
      ? textColor
      : textColor.replace(/^text-/, "border-");
  return mergeColorProps(
    colorProps(bgColor, "backgroundColor"),
    colorProps(borderSource, "borderColor"),
    colorProps(textColor, "color"),
  );
}

/**
 * Fondo + texto para los botones rápidos de "Cambiar estado". El estado activo se pinta "relleno"
 * (fondo oscuro + texto blanco): en Tailwind se deriva oscureciendo el bgColor ('100'→'600', legado,
 * sin cambios); en HEX no existe esa variante generada, así que se reutiliza el textColor (ya es un
 * tono oscuro/saturado) como fondo sólido, que da un resultado visualmente equivalente.
 */
export function statusButtonColorProps(
  bgColor: string,
  textColor: string,
  isActive: boolean,
): ColorProps {
  const isHex = bgColor.startsWith("#") || textColor.startsWith("#");
  if (!isHex) {
    return {
      className: isActive
        ? `${bgColor.replace("100", "600")} text-white`
        : `${bgColor} ${textColor} hover:opacity-80`,
      style: {},
    };
  }
  if (isActive) {
    return {
      className: "text-white",
      style: { backgroundColor: textColor },
    };
  }
  return mergeColorProps(
    colorProps(bgColor, "backgroundColor"),
    colorProps(textColor, "color"),
  );
}
